import { splitLastOccurrence } from "../../../Helpers/StringHelpers";
import { iterateEntries, iterateKeys, IterUtils } from "../../../../../Common/IterableUtils";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    convertToAbsoluteModelPath,
    convertToLegacyPath,
    createModelPath,
    ModelPath,
    PathTokens,
    SimplePathToken,
} from "../../../../../Common/ModelPath/ModelPath";
import type { BindingPath, ViewModel } from "../CoreTypes";
import { FormViewModel, NodeViewModel } from "../Store/FormViewModel";
import {
    ServerJsonResponse,
    ServerJsonResponseItem,
} from "../../../../../Common/CandyApiClient/Types/GetInner/ServerInnerResponse";
import {
    isServerRawResponseRootItem,
    ServerRawResponse,
    ServerRawResponseItem,
    ServerRawResponseRootItem,
} from "../../../../../Common/CandyApiClient/Types/GetInner/ServerRawResponse";
import { getPathsViewModels, parseStringViewModel } from "../../../Helpers/ViewModelHelpers";

import { IReadonlyModelHunk, TokenNode } from "./IReadonlyModelHunk";
import type { ModelChunk, ModelValuesBatch, ReadonlyModelChunk, ReadonlyModelValuesBatch } from "./ModelChunk";

export function isModelHunk(
    modelChunkOrHunk: ReadonlyModelChunk | IReadonlyModelHunk
): modelChunkOrHunk is IReadonlyModelHunk;
export function isModelHunk(modelChunkOrHunk: ModelChunk | ModelHunk): modelChunkOrHunk is ModelHunk;
export function isModelHunk(
    modelChunkOrHunk: ReadonlyModelChunk | IReadonlyModelHunk | ModelHunk | ModelChunk
): modelChunkOrHunk is IReadonlyModelHunk | ModelChunk {
    return modelChunkOrHunk instanceof ModelHunk;
}

export class ModelHunk implements IReadonlyModelHunk {
    private readonly tokenNode: Map<SimplePathToken, TokenNode> = new Map();

    public static create(): ModelChunk {
        return {};
    }

    public static paths(chunk: ReadonlyModelChunk | IReadonlyModelHunk): BindingPath[] {
        return Iterator.from(this.iteratePaths(chunk)).toArray();
    }

    public static iteratePaths(chunk: ReadonlyModelChunk | IReadonlyModelHunk): Iterable<BindingPath> {
        if (isModelHunk(chunk)) {
            return Iterator.from(chunk.iteratePaths()).map(x => x.toLegacyPath());
        } else {
            return iterateKeys(chunk);
        }
    }

    public static get(chunk: ReadonlyModelChunk | IReadonlyModelHunk, path: BindingPath): undefined | ViewModel {
        if (isModelHunk(chunk)) {
            return chunk.get(createModelPath(path, true));
        } else {
            return chunk[path];
        }
    }

    public static set(
        chunk: ModelChunk | ModelHunk,
        path: BindingPath | AbsoluteModelPath,
        viewModel: undefined | ViewModel
    ): void {
        if (isModelHunk(chunk)) {
            chunk.set(convertToAbsoluteModelPath(path), viewModel);
        } else {
            chunk[convertToLegacyPath(path)] = viewModel;
        }
    }

    public static updateIfExists(
        chunk: ModelChunk | ModelHunk,
        path: BindingPath,
        updateFunc: (viewModel: ViewModel) => void
    ): void {
        if (isModelHunk(chunk)) {
            chunk.updateIfExists(createModelPath(path, true), updateFunc);
        } else {
            const viewModel = chunk[path];
            if (viewModel) {
                updateFunc(viewModel);
            }
        }
    }

    public static *iterateEntries(chunk: ReadonlyModelChunk | IReadonlyModelHunk): Iterable<[BindingPath, ViewModel]> {
        if (isModelHunk(chunk)) {
            yield* Iterator.from(chunk.iterateEntries()).map<[BindingPath, ViewModel]>(([path, viewModel]) => [
                path.toLegacyPath(),
                viewModel,
            ]);
        } else {
            for (const [path, value] of iterateEntries(chunk)) {
                if (value != undefined) {
                    yield [path, value];
                }
            }
        }
    }

    public static *iterateEntriesNew(
        chunk: ReadonlyModelChunk | IReadonlyModelHunk
    ): Iterable<[AbsoluteResolvedModelPath, ViewModel]> {
        if (isModelHunk(chunk)) {
            yield* chunk.iterateEntries();
        } else {
            for (const [path, value] of iterateEntries(chunk)) {
                if (value != undefined) {
                    yield [createModelPath(path, true), value];
                }
            }
        }
    }

    public static isEmpty(chunk: ReadonlyModelChunk | IReadonlyModelHunk): boolean {
        if (isModelHunk(chunk)) {
            return chunk.isEmpty();
        } else {
            return IterUtils.isEmpty(iterateKeys(chunk));
        }
    }

    public static hasPath(chunk: ReadonlyModelChunk | IReadonlyModelHunk, path: BindingPath): boolean {
        if (isModelHunk(chunk)) {
            return chunk.hasPath(createModelPath(path, true));
        } else {
            return Boolean(chunk[path]);
        }
    }

    public static remove(chunk: ModelChunk | ModelHunk, path: BindingPath): void {
        if (isModelHunk(chunk)) {
            chunk.remove(createModelPath(path, true));
        } else {
            delete chunk[path];
        }
    }

    public static createFromValuesBatch(valuesBatch: ReadonlyModelValuesBatch): ModelChunk {
        return valuesBatch;
    }

    public static createHunkFromValuesBatch(valuesBatch: ReadonlyModelValuesBatch): ModelHunk {
        const result = new ModelHunk();
        for (const [path, value] of iterateEntries(valuesBatch)) {
            result.set(createModelPath(path, true), value);
        }
        return result;
    }

    public static toValuesBatch(chunk: ModelChunk): ModelValuesBatch {
        return chunk;
    }

    public getTokenNodes(): Readonly<Map<SimplePathToken, TokenNode>> {
        return this.tokenNode;
    }

    public isEmpty(): boolean {
        return this.tokenNode.size === 0;
    }

    public *iteratePaths(): Iterable<AbsoluteResolvedModelPath> {
        yield* this.iterateChildTokenPaths(undefined, this.tokenNode);
    }

    public *iterateEntries(): Iterable<[AbsoluteResolvedModelPath, ViewModel]> {
        yield* this.iterateChildTokenEntries(undefined, this.tokenNode);
    }

    public updateIfExists(path: AbsoluteResolvedModelPath, updateFunc: (viewModel: ViewModel) => void): void {
        const currentNode = this.getTokenNode(path);
        if (currentNode?.viewModel != undefined) {
            updateFunc(currentNode.viewModel);
        }
    }

    public set(path: ModelPath, viewModel: undefined | ViewModel): void {
        const currentNode = this.getOrCreateTokenNode(path);
        currentNode.viewModel = viewModel;
    }

    public getOrCreate(path: ModelPath, viewModel: ViewModel): ViewModel {
        const currentNode = this.getOrCreateTokenNode(path);
        return currentNode.viewModel ?? (currentNode.viewModel = viewModel);
    }

    public remove(path: ModelPath): void {
        let currentNodeParentMap: undefined | Map<SimplePathToken, TokenNode>;
        let currentNode: undefined | TokenNode;
        let currentNodeToken: undefined | SimplePathToken;
        for (const pathToken of path.getPathParts()) {
            if (!PathTokens.isSimpleToken(pathToken)) {
                throw new Error("Path spec is not supported in model hunk");
            }

            if (currentNode == undefined) {
                const nextNode = this.tokenNode.get(pathToken);
                if (nextNode == undefined) {
                    return;
                }
                currentNodeToken = pathToken;
                currentNodeParentMap = this.tokenNode;
                currentNode = nextNode;
            } else {
                if (currentNode.childNodes == undefined) {
                    currentNode.childNodes = new Map();
                }
                const nextNode = currentNode.childNodes.get(pathToken);
                if (nextNode == undefined) {
                    return;
                }
                currentNodeToken = pathToken;
                currentNodeParentMap = currentNode.childNodes;
                currentNode = nextNode;
            }
        }
        if (currentNodeParentMap != undefined && currentNodeToken != undefined) {
            currentNodeParentMap.delete(currentNodeToken);
        }
    }

    public toModelChunk(): ModelChunk {
        const result = ModelHunk.create();
        for (const [path, value] of this.iterateEntries()) {
            ModelHunk.set(result, path.toLegacyPath(), value);
        }
        return result;
    }

    public get(path: ModelPath): undefined | ViewModel {
        const currentNode = this.getTokenNode(path);
        return currentNode?.viewModel;
    }

    public hasPath(path: ModelPath): boolean {
        return this.getTokenNode(path)?.viewModel != undefined;
    }

    private *iterateChildTokenPaths(
        prefix: undefined | AbsoluteResolvedModelPath,
        tokenNodesMap: Map<SimplePathToken, TokenNode>
    ): Iterable<AbsoluteResolvedModelPath> {
        for (const [token, tokenNode] of tokenNodesMap) {
            const currentPath = prefix == undefined ? createModelPath(token, true) : prefix.joinWith(token);
            if (tokenNode.viewModel != undefined) {
                yield currentPath;
            }
            if (tokenNode.childNodes != undefined) {
                yield* this.iterateChildTokenPaths(currentPath, tokenNode.childNodes);
            }
        }
    }

    private *iterateChildTokenEntries(
        prefix: undefined | AbsoluteResolvedModelPath,
        tokenNodesMap: Map<SimplePathToken, TokenNode>
    ): Iterable<[AbsoluteResolvedModelPath, ViewModel]> {
        for (const [token, tokenNode] of tokenNodesMap) {
            const currentPath = prefix == undefined ? createModelPath(token, true) : prefix.joinWith(token);
            if (tokenNode.viewModel != undefined) {
                yield [currentPath, tokenNode.viewModel];
            }
            if (tokenNode.childNodes != undefined) {
                yield* this.iterateChildTokenEntries(currentPath, tokenNode.childNodes);
            }
        }
    }

    private getOrCreateTokenNode(path: ModelPath): TokenNode {
        let currentNode: undefined | TokenNode;
        for (const pathToken of path.getPathParts()) {
            if (!PathTokens.isSimpleToken(pathToken)) {
                throw new Error("Path spec is not supported in model hunk");
            }

            if (currentNode == undefined) {
                let nextNode = this.tokenNode.get(pathToken);
                if (nextNode == undefined) {
                    nextNode = {};
                    this.tokenNode.set(pathToken, nextNode);
                }
                currentNode = nextNode;
            } else {
                if (currentNode.childNodes == undefined) {
                    currentNode.childNodes = new Map();
                }
                let nextNode = currentNode.childNodes.get(pathToken);
                if (nextNode == undefined) {
                    nextNode = {};
                    currentNode.childNodes.set(pathToken, nextNode);
                }
                currentNode = nextNode;
            }
        }
        if (currentNode == undefined) {
            throw new Error("Empty path is invalid");
        }
        return currentNode;
    }

    private getTokenNode(path: ModelPath): undefined | TokenNode {
        let currentNode: undefined | TokenNode;
        for (const pathToken of path.getPathParts()) {
            if (!PathTokens.isSimpleToken(pathToken)) {
                throw new Error("Path spec is not supported in model hunk");
            }

            if (currentNode == undefined) {
                const nextNode = this.tokenNode.get(pathToken);
                if (nextNode == undefined) {
                    return undefined;
                }
                currentNode = nextNode;
            } else {
                if (currentNode.childNodes == undefined) {
                    currentNode.childNodes = new Map();
                }
                const nextNode = currentNode.childNodes.get(pathToken);
                if (nextNode == undefined) {
                    return undefined;
                }
                currentNode = nextNode;
            }
        }
        return currentNode;
    }

    public mergeIntoViewModel(nextViewModelState: FormViewModel): FormViewModel {
        if (this.isEmpty()) {
            return nextViewModelState;
        }
        const result = { ...nextViewModelState };
        for (const [childToken, childNode] of this.tokenNode.entries()) {
            // @ts-ignore child token не содержит ., поэтому тут всё норм
            result[childToken] = this.mergeIntoViewModelInternal(result[childToken], childNode);
        }
        return result;
    }

    public fillFromServerJsonResponse(serverResponse: ServerJsonResponse): void {
        if (!this.isEmpty()) {
            throw new Error("Hunk must be empty before fill with server data");
        }

        function buildNodeFromServerResponse(value: ServerJsonResponseItem): TokenNode {
            const result: TokenNode = {};

            for (const [fieldName, fieldValue] of iterateEntries(value)) {
                if (fieldName === ".") {
                    // @ts-expect-error
                    result.viewModel = fieldValue;
                } else {
                    if (result.childNodes == undefined) {
                        result.childNodes = new Map();
                    }
                    // @ts-expect-error
                    result.childNodes.set(fieldName, buildNodeFromServerResponse(fieldValue));
                }
            }
            return result;
        }

        for (const [fieldName, value] of iterateEntries(serverResponse)) {
            this.tokenNode.set(fieldName, buildNodeFromServerResponse(value));
        }
    }

    public fillFromServerRawResponse(serverResponse: ServerRawResponse): void {
        if (!this.isEmpty()) {
            throw new Error("Hunk must be empty before fill with server data");
        }

        const fillTokenNodesMap = (
            pathTokens: SimplePathToken[],
            viewModel: ViewModel,
            tokenNodes: Map<SimplePathToken, TokenNode>
        ): void => {
            if (pathTokens.length === 0 || pathTokens[0] == undefined) {
                return;
            }
            const pathToken = pathTokens[0];
            if (!tokenNodes.has(pathToken)) {
                tokenNodes.set(pathToken, {});
            }

            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const tokenNode = tokenNodes.get(pathToken)!;
            if (pathTokens.length === 1) {
                tokenNode.viewModel = viewModel;
            } else {
                if (tokenNode.childNodes == undefined) {
                    tokenNode.childNodes = new Map();
                }
                const remainedPath = pathTokens.slice(1);
                fillTokenNodesMap(remainedPath, viewModel, tokenNode.childNodes);
            }
        };

        const buildRootNodeFromRawServerResponse = (item: ServerRawResponseRootItem): void => {
            for (const [path, stringViewModel] of iterateEntries(getPathsViewModels(item["@"]))) {
                const viewModel = parseStringViewModel(stringViewModel);
                fillTokenNodesMap(path.split("/"), viewModel, this.tokenNode);
            }
        };

        const buildNodeFromRawServerResponse = (item: ServerRawResponseItem): void => {
            for (const [pathWithInstance, model] of iterateEntries(item)) {
                const [multiplicityPath, instance] = splitLastOccurrence(pathWithInstance, "@");
                const pathTokensBase: SimplePathToken[] = multiplicityPath
                    .split("/")
                    .concat(Number(instance).toString());

                for (const [viewModelPath, stringViewModel] of iterateEntries(getPathsViewModels(model))) {
                    const instancePathTokens: SimplePathToken[] = pathTokensBase
                        .concat(viewModelPath.split("/"))
                        .filter(Boolean);
                    const viewModel = parseStringViewModel(stringViewModel);
                    fillTokenNodesMap(instancePathTokens, viewModel, this.tokenNode);
                }
            }
        };

        for (const item of serverResponse) {
            if (isServerRawResponseRootItem(item)) {
                buildRootNodeFromRawServerResponse(item);
            } else {
                buildNodeFromRawServerResponse(item);
            }
        }
    }

    public mergeIntoViewModelInternal(nodeViewModel: NodeViewModel, tokenNode: TokenNode): NodeViewModel {
        const result = { ...nodeViewModel };
        if (tokenNode.viewModel != undefined) {
            result["."] = { ...(result["."] ?? {}), ...tokenNode.viewModel };
        }
        if (tokenNode.childNodes != undefined) {
            for (const [childToken, childNode] of tokenNode.childNodes.entries()) {
                // @ts-ignore child token не содержит ., поэтому тут всё норм
                result[childToken] = this.mergeIntoViewModelInternal(result[childToken], childNode);
            }
        }
        return result;
    }
}
